/**
 * 指标元信息常量
 *
 * 需求 §4.4.3 / §4.4.4 / §4.4.5 / §4.4.6
 * ⚠️ D17：指标字段全英文 snake_case；label 英文文案同时作为 i18n key
 */
import {
  adCreativesComparisonChannelShareTipsMap,
  adCreativesComparisonCompetitorShareTipsMap,
  engagementStatTipsMap,
} from '@opm/components/BubbleCommon';
import { IMetricMeta, TDateType, TMetric } from '../types/common';

/** 指标元信息映射 */
export const METRIC_METAS: Record<TMetric, IMetricMeta> = {
  all_creative: {
    key: 'all_creative',
    label: {
      daily: 'Active Ads',
      weekly: 'Avg. Weekly Active Ads',
      monthly: 'Avg. Monthly Active Ads',
    },
    pieLabel: {
      single: 'Active Ads',
      multi: 'Avg. Daily Active Ads',
    },
    formatter: 'reviews',
    yFormatter: 'reviews',
  },
  new_creative: {
    key: 'new_creative',
    label: {
      daily: 'New Ads',
      weekly: 'Accumulated Weekly New Ads',
      monthly: 'Accumulated Monthly New Ads',
    },
    pieLabel: {
      single: 'New Ads',
      multi: 'Accumulated Daily New Ads',
    },
    formatter: 'reviews',
    yFormatter: 'reviews',
  },
  all_material: {
    key: 'all_material',
    label: {
      daily: 'Active Creatives',
      weekly: 'Avg. Weekly Active Creatives',
      monthly: 'Avg. Monthly Active Creatives',
    },
    pieLabel: {
      single: 'Active Creatives',
      multi: 'Avg. Daily Active Creatives',
    },
    formatter: 'reviews',
    yFormatter: 'reviews',
  },
  new_material: {
    key: 'new_material',
    label: {
      daily: 'New Creatives',
      weekly: 'Accumulated Weekly New Creatives',
      monthly: 'Accumulated Monthly New Creatives',
    },
    pieLabel: {
      single: 'New Creatives',
      multi: 'Accumulated New Creatives',
    },
    formatter: 'reviews',
    yFormatter: 'reviews',
  },
  engagement_stat: {
    key: 'engagement_stat',
    label: {
      daily: 'Cumulative Impressions',
      weekly: 'Accumulated Weekly Cumulative Impressions',
      monthly: 'Accumulated Monthly Cumulative Impressions',
    },
    formatter: 'reviews',
    yFormatter: 'reviews',
    tipsMap: engagementStatTipsMap,
  },

  creative_count_stat: {
    key: 'creative_count_stat',
    label: {
      daily: 'Active Ads',
      daily_range: 'Avg. Daily Active Ads',
      weekly: 'Avg. Weekly Active Ads',
      monthly: 'Avg. Monthly Active Ads',
    },
    formatter: 'reviews',
    yFormatter: 'reviews',
  },
  creative_count_stat_by_channel_percent: {
    key: 'creative_count_stat_by_channel_percent',
    label: {
      daily: 'Active Ads Channel Share',
      daily_range: 'Avg. Active Ads Channel Share',
      weekly: 'Avg. Weekly Active Ads Channel Share',
      monthly: 'Avg. Monthly Active Ads Channel Share',
    },
    formatter: 'ratio',
    yFormatter: 'percent',
    tipsMap: adCreativesComparisonChannelShareTipsMap,
  },
  creative_count_stat_by_competitor_percent: {
    key: 'creative_count_stat_by_competitor_percent',
    label: {
      daily: 'Active Ads Competitor Share',
      daily_range: 'Avg. Active Ads Competitor Share',
      weekly: 'Avg. Weekly Active Ads Competitor Share',
      monthly: 'Avg. Monthly Active Ads Competitor Share',
    },
    formatter: 'ratio',
    yFormatter: 'percent',
    tipsMap: adCreativesComparisonCompetitorShareTipsMap,
  },
  creative_score_stat: {
    key: 'creative_score_stat',
    label: {
      daily: 'Active Ads Estimated Heat',
      daily_range: 'Avg. Active Ads Estimated Heat',
      weekly: 'Avg. Weekly Active Ads Estimated Heat',
      monthly: 'Avg. Monthly Active Ads Estimated Heat',
    },
    formatter: 'score',
    yFormatter: 'reviews',
  },
  impression_stat: {
    key: 'impression_stat',
    label: {
      daily: 'Total Impressions on Active Ads',
      daily_range: 'Avg. Total Impressions on Active Ads',
      weekly: 'Avg. Weekly Total Impressions on Active Ads',
      monthly: 'Avg. Monthly Total Impressions on Active Ads',
    },
    formatter: 'reviews',
    yFormatter: 'reviews',
  },
};

/**
 * 从 IMetricMeta.label 提取字符串文案
 *
 * label 支持两种格式：
 * - string：直接返回
 * - { daily, weekly, monthly }：根据 dateType 取对应键值
 *
 * @param meta  指标元信息
 * @param dateType  当前时间粒度，用于对象格式 label 的键选择
 * @returns 国际化用的 label 字符串
 */
export const getMetricLabel = (
  meta: IMetricMeta | undefined,
  dateType?: TDateType,
): string => {
  if (!meta) return '';
  const { label } = meta;
  if (typeof label === 'object') {
    return dateType ? label[dateType] : '';
  }
  return label;
};

/**
 * KPI 汇总表的指标列（需求 §4.4.3）
 * 接口契约 specs/多游戏投放对比页/api/overview.md：
 * 4 个指标，顺序与表头一致。
 */
export const KPI_OVERVIEW_METRICS: TMetric[] = [
  'all_creative',
  'new_creative',
  'all_material',
  'new_material',
];

/** Module A 趋势图可切换指标（需求 §4.4.4） */
export const TREND_METRICS: TMetric[] = [
  'all_creative',
  'new_creative',
  'all_material',
  'new_material',
  'engagement_stat',
];

/** Module A 默认指标 */
export const DEFAULT_TREND_METRIC: TMetric = 'all_creative';

/**
 * Module B 可切换指标（需求 §4.4.5）
 * 接口契约 specs/多游戏投放对比页/api/sum.md / timeline.md：
 * - timeline 接口（line / bar / pie）：metric 用 sum 接口同名值
 * - sum 接口（ranking）：5 个 metric 一次拉取
 */
export const CHANNEL_METRICS: TMetric[] = [
  'creative_count_stat',
  'creative_count_stat_by_channel_percent',
  'creative_count_stat_by_competitor_percent',
  'creative_score_stat',
  'impression_stat',
];

/** Module B 默认指标 */
export const DEFAULT_CHANNEL_METRIC: TMetric = 'creative_count_stat';

/** Module C 唯一指标（需求 §4.4.6：不可切换） */
export const COUNTRY_METRIC: TMetric = 'all_creative';

/** 9999%+ 封顶阈值（@op/utils/value 的 percentageFormat 已内置同样逻辑） */
export const QOQ_MAX_CAP = 9999;

/** 分页默认值（Module B，D11） */
export const DEFAULT_PAGE_SIZE = 20;

/**
 * KPI 汇总表（Module Overview）：接口契约对齐后**不分页**
 * 游戏粒度全量返回（≤15 条），无需 page / page_size。
 */
